import { useEffect, useRef, useState } from "react";

export type EntranceMotion = "static" | "armed" | "play";

/** Motion state for a graphic whose finished frame is the server-rendered
 * default. "armed" hides it until it scrolls into view, then "play" runs the
 * entrance and the ambient motion. A graphic already on screen at load (say,
 * after a reload restores the scroll) plays straight away, so its finished
 * frame shows briefly before it redraws. Reduced motion stays "static".
 */
export function useEntranceMotion<T extends Element>(threshold = 0.45) {
  const ref = useRef<T>(null);
  const [motion, setMotion] = useState<EntranceMotion>("static");

  useEffect(() => {
    const node = ref.current;
    if (!node || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const { top, bottom } = node.getBoundingClientRect();
    if ((top < window.innerHeight && bottom > 0) || !("IntersectionObserver" in window)) {
      setMotion("play");
      return;
    }
    setMotion("armed");
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setMotion("play");
        observer.disconnect();
      },
      { threshold },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, motion] as const;
}
